import { api } from './index'

export type RevenueCenterState = 'ACTIVE' | 'ARCHIVED' | 'UNKNOWN'

export type Handoff =
  | 'PICKUP'
  | 'DELIVERY'
  | 'CURBSIDE'
  | 'TABLESIDE'
  | 'DRIVETHRU'

export type RevenueCenter = {
  provider_uid: string
  name: string
  description: string
  state: RevenueCenterState
}

export type RevenueCenterConfigRow = {
  // Null/undefined means the row applies at the merchant-default scope.
  // A populated value scopes to that handoff mode.
  handoff?: Handoff | null
  revenue_center_name: string
}

type GetRevenueCentersResponse = {
  revenue_centers: RevenueCenter[]
}

type GetRevenueCenterConfigResponse = {
  revenue_center_configs: RevenueCenterConfigRow[]
  // ISO8601 timestamp of the most recent catalog sync, or null if the
  // catalog has never been synced from the ordering provider.
  synced_at?: string | null
  supported_handoffs: Handoff[]
}

type UpsertPayload = {
  revenue_center_name: string
  handoff?: Handoff
}

type DeletePayload = {
  handoff?: Handoff
}

export const revenueCentersApi = api.injectEndpoints({
  overrideExisting: false,
  endpoints: build => ({
    /**
     * Lists the merchant's ordering-provider Revenue Center catalog. Empty
     * includeStates defaults server-side to [ACTIVE] for the picker;
     * pass [ACTIVE, ARCHIVED] when rendering the dashboard archived-RC
     * warning state so the original name can resolve.
     */
    getRevenueCenters: build.query<
      GetRevenueCentersResponse,
      { includeStates?: RevenueCenterState[] }
    >({
      query: ({ includeStates = [] } = {}) => {
        const params = new URLSearchParams()
        includeStates.forEach(s => params.append('include_states[]', s))
        const qs = params.toString()
        return `revenue_centers${qs ? `?${qs}` : ''}`
      },
      providesTags: ['RevenueCenter'],
    }),

    /**
     * Returns the merchant's current Revenue Center config — the
     * (optional) merchant default plus zero or more per-handoff override
     * rows.
     */
    getRevenueCenterConfig: build.query<GetRevenueCenterConfigResponse, void>({
      query: () => 'revenue_center_config',
      providesTags: ['RevenueCenterConfig'],
    }),

    /**
     * Upserts one config row. Omit `handoff` to upsert the merchant
     * default scope; set it to upsert a per-handoff override.
     */
    upsertRevenueCenterConfig: build.mutation<void, UpsertPayload>({
      query: payload => ({
        url: 'revenue_center_config',
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: ['RevenueCenterConfig'],
    }),

    /**
     * Deletes one config row. Omit `handoff` to delete the merchant
     * default; set it to delete a per-handoff override.
     */
    deleteRevenueCenterConfig: build.mutation<void, DeletePayload>({
      query: payload => ({
        url: 'revenue_center_config',
        method: 'DELETE',
        body: payload,
      }),
      invalidatesTags: ['RevenueCenterConfig'],
    }),

    /**
     * Triggers an asynchronous re-sync of the Revenue Center catalog from
     * the ordering provider. Returns 202 immediately; the catalog and
     * synced_at update once the background job completes, so both the
     * catalog and config tags are invalidated to refetch.
     */
    refreshRevenueCenters: build.mutation<void, void>({
      query: () => ({
        url: 'revenue_centers/refresh',
        method: 'POST',
      }),
      invalidatesTags: ['RevenueCenter', 'RevenueCenterConfig'],
    }),
  }),
})

export const {
  useGetRevenueCentersQuery,
  useGetRevenueCenterConfigQuery,
  useUpsertRevenueCenterConfigMutation,
  useDeleteRevenueCenterConfigMutation,
  useRefreshRevenueCentersMutation,
} = revenueCentersApi
